/**
 * Validation for notification redirect links to prevent open-redirect / DOM-XSS
 * (CWE-601). Notification payloads originate from an unauthenticated endpoint,
 * so the `redirectLink` is treated as untrusted input. Only http(s) URLs that
 * resolve to an Amazon-owned host are allowed to drive navigation.
 *
 * This blocks dangerous schemes such as `javascript:` (DOM XSS), `data:`,
 * `intent://` (launch arbitrary Android components) and `app://` (kiosk escape),
 * as well as phishing redirects to non-Amazon hosts.
 */

// Allowed URL schemes for navigation.
const ALLOWED_PROTOCOLS = ["https:", "http:"];

// Host suffixes that are considered Amazon-owned. A host is allowed only if it
// equals one of these (minus the leading dot) or is a sub-domain of it.
const ALLOWED_HOST_SUFFIXES = [".amazon.dev", ".amazon.com", ".amazonaws.com"];

/**
 * Returns true when `redirectLink` is a syntactically valid http(s) URL whose
 * host is Amazon-owned, and is therefore safe to navigate to.
 *
 * @param {string} redirectLink raw, untrusted URL from the notification payload
 * @returns {boolean}
 */
export const isAllowedRedirectUrl = (redirectLink) => {
    if (!redirectLink || typeof redirectLink !== "string") {
        return false;
    }

    let url;
    try {
        url = new URL(redirectLink);
    } catch (e) {
        // Not a parseable absolute URL (relative links, malformed input, etc.).
        return false;
    }

    if (!ALLOWED_PROTOCOLS.includes(url.protocol)) {
        return false;
    }

    const hostname = url.hostname.toLowerCase();
    return ALLOWED_HOST_SUFFIXES.some(suffix => {
        // Allow the apex domain (e.g. "amazon.com") and any sub-domain
        // (e.g. "foo.amazon.com") while rejecting look-alikes
        // (e.g. "evil-amazon.com" or "amazon.com.attacker.com").
        const apex = suffix.slice(1);
        return hostname === apex || hostname.endsWith(suffix);
    });
};

export default isAllowedRedirectUrl;
